import {
  MAPPING_ACTION_FIELDS,
  MAPPING_ATYPE,
  MAPPING_TIMED_ACTIONS,
  MAPPING_TRIGGER,
  fieldSeparatorFor,
  repeatSeparatorFor,
} from '../../catalog/mapping';
import type { MappingActionKey } from '../../catalog/mapping';
import { ISSUE, ISSUE_TARGET, configIssue } from '../../catalog/issue';
import type { ConfigIssue } from '../../catalog/issue';
import { fail, ok } from '../../result';
import type { Result } from '../../result';
import { byteList, timeoutIssue } from '../commands/validate';
import type { RcuSceneEntry } from '../../wire';

/**
 * Building a scene's action strings.
 *
 * A scene carries one string per action kind, e.g. `a: "1:1;2:0"` and
 * `d: "10,1,1,1"`. The firmware parses them positionally and truncates rather
 * than refusing, so the console assembles them here, once, with the arity and
 * ranges from the catalog, and refuses anything it cannot spell exactly.
 */

/** One action, as the editor holds it: a field and its numbers. */
export interface ActionDraft {
  readonly key: MappingActionKey;
  readonly parts: readonly number[];
}

const isTimed = (key: MappingActionKey): boolean =>
  MAPPING_TIMED_ACTIONS.some((timed) => timed === key);

const actionIssue = (message: string): ConfigIssue =>
  configIssue(ISSUE.COMMAND_FIELD_OUT_OF_RANGE, { kind: ISSUE_TARGET.RCU }, message);

/**
 * The issue for a draft whose arity, numbers or timeout the firmware could not
 * hold. Every field is a byte except a timed action's last one, which is seconds
 * and may run past 255 — the firmware reads it as a longer integer.
 */
const partsIssue = (draft: ActionDraft): ConfigIssue | null => {
  const expected = MAPPING_ACTION_FIELDS[draft.key];
  if (draft.parts.length !== expected) {
    return actionIssue(
      `${draft.key} takes ${expected} numbers, not ${draft.parts.length}`,
    );
  }
  const timed = isTimed(draft.key);
  const byteParts = timed ? draft.parts.slice(0, -1) : draft.parts;
  const bytes = byteList(draft.key, byteParts, byteParts.length);
  if (bytes !== null) return bytes;
  return timed ? timeoutIssue(draft.parts[expected - 1] ?? 0) : null;
};

/** One action string, e.g. `1:1` or `10,1,1,1`. */
export const encodeAction = (draft: ActionDraft): Result<string> => {
  const issue = partsIssue(draft);
  return issue === null ? ok(draft.parts.join(fieldSeparatorFor(draft.key))) : fail(issue);
};

/**
 * Several repeats of one action kind, e.g. `1:1,2:0` for relays or
 * `10,1,1,1;4,1,2,1` for DIN channels.
 */
export const encodeActionField = (
  key: MappingActionKey,
  repeats: readonly (readonly number[])[],
): Result<string> => {
  if (repeats.length === 0) return fail(actionIssue(`${key} needs at least one action`));

  const encoded: string[] = [];
  for (const parts of repeats) {
    const action = encodeAction({ key, parts });
    if (!action.ok) return action;
    encoded.push(action.value);
  }
  return ok(encoded.join(repeatSeparatorFor(key)));
};

/** A scene as the editor holds it, before it becomes a document entry. */
export interface SceneDraft {
  readonly trigger: readonly number[];
  /** `k`: zero suppresses every action of the entry. */
  readonly key: number | null;
  readonly actions: readonly ActionDraft[];
}

/**
 * The scene entry the firmware expects. Each action kind becomes one string, so
 * two relay actions in the editor merge into one `a` field — which is exactly how
 * the firmware reads repeats.
 */
export const encodeScene = (draft: SceneDraft): Result<RcuSceneEntry> => {
  const trigger = byteList('Trigger', draft.trigger, MAPPING_TRIGGER.LENGTH);
  if (trigger !== null) return fail(trigger);

  if (draft.key !== null) {
    const key = byteList('Key', [draft.key], 1);
    if (key !== null) return fail(key);
  }

  if (draft.actions.length === 0) {
    return fail(actionIssue('A scene needs at least one action'));
  }

  const grouped = new Map<MappingActionKey, number[][]>();
  for (const action of draft.actions) {
    const repeats = grouped.get(action.key) ?? [];
    repeats.push([...action.parts]);
    grouped.set(action.key, repeats);
  }

  const fields: Partial<Record<MappingActionKey, string>> = {};
  for (const [key, repeats] of grouped) {
    const encoded = encodeActionField(key, repeats);
    if (!encoded.ok) return encoded;
    fields[key] = encoded.value;
  }

  const base = { atype: MAPPING_ATYPE.SCENE, x: [...draft.trigger], ...fields };
  const entry: RcuSceneEntry = draft.key === null ? base : { ...base, k: draft.key };
  return ok(entry);
};
